import { useMemo, useState } from "react";
import { CodeBlock } from "@/components/CodeBlock";
import { ByteView, Field, FieldGroup, TextPreview } from "@/components/HashDisplay";
import { formatBytes, toHex } from "@/hashtree/bytes";
import { chkBlossomUri, encryptCHK } from "@/hashtree/chk";
import type { BuiltNode } from "@/hashtree/nodes";
import type { HashtreeBuild } from "@/hashtree/useHashtreeBuild";
import { Slide, SpecNote } from "./Slide";

/** Beyond this many chunks, a button each stops being a usable control. */
const CHUNK_BUTTON_LIMIT = 48;

/** Every leaf chunk in the tree, in order. */
function leafChunks(node: BuiltNode): BuiltNode[] {
  if (node.kind === "blob") return [node];
  return node.children.flatMap(leafChunks);
}

export function EncryptSlide({ build, onNext }: { build: HashtreeBuild; onNext: () => void }) {
  const { tree, file } = build;
  const [index, setIndex] = useState(0);
  const [repeated, setRepeated] = useState<string | null>(null);

  // Walking the tree on every render is wasted work when only the selection
  // changed, and a small chunk size can put thousands of nodes in here.
  const chunks = useMemo(() => (tree ? leafChunks(tree.file) : []), [tree]);

  if (!tree || !file || chunks.length === 0) return null;

  const active = chunks[Math.min(index, chunks.length - 1)]!;

  return (
    <Slide
      bud={15}
      title="Encrypt each chunk"
      lede={
        <>
          The encryption key is the hash of the content itself. That sounds circular, but it is what keeps deduplication
          working: the same bytes always produce the same key, the same ciphertext, and therefore the same Blossom hash.
        </>
      }
    >
      <CodeBlock
        caption="chk-v1, the whole algorithm"
        code={`const chkKey = sha256(plaintext);
const aesKey = hkdfSha256(chkKey, "hashtree-chk", "encryption-key", 32);
const ciphertext = aesGcm(aesKey, ZERO_NONCE_12_BYTES, plaintext);
const blobHash = sha256(ciphertext);   // what the server stores it under`}
      />

      <SpecNote tone="warn" title="Why a zero nonce is safe here, and only here">
        Reusing a nonce normally destroys AES-GCM. It is safe in this one construction because the key is derived from
        the plaintext, so a given key can only ever encrypt one plaintext. Using <code>chk-v1</code> with a key you
        picked yourself would break it badly.
      </SpecNote>

      {chunks.length > 1 && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {chunks.slice(0, CHUNK_BUTTON_LIMIT).map((chunk, position) => (
              <button
                key={chunk.id}
                type="button"
                onClick={() => {
                  setIndex(position);
                  setRepeated(null);
                }}
                className={`rounded-md border px-2 py-1 font-mono text-xs transition-colors ${
                  position === index ? "border-primary bg-primary/10 font-medium" : "hover:bg-muted"
                }`}
              >
                chunk {position}
              </button>
            ))}
          </div>

          {/* A small chunk size on a large file makes thousands of chunks, so
              past a point they get a jump box instead of a button each. */}
          {chunks.length > CHUNK_BUTTON_LIMIT && (
            <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {chunks.length - CHUNK_BUTTON_LIMIT} more chunks — jump to
              <input
                type="number"
                min={0}
                max={chunks.length - 1}
                value={index}
                onChange={(event) => {
                  const wanted = Number(event.target.value);
                  if (Number.isFinite(wanted)) {
                    setIndex(Math.max(0, Math.min(chunks.length - 1, wanted)));
                    setRepeated(null);
                  }
                }}
                className="w-24 rounded-md border bg-background px-2 py-1 font-mono tabular-nums"
              />
            </label>
          )}
        </div>
      )}

      <div className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex items-baseline justify-between">
          <p className="text-sm font-medium">
            {chunks.length > 1 ? `Chunk ${index}` : file.name}
            <span className="ml-2 text-xs text-muted-foreground">{formatBytes(active.plaintext.length)}</span>
          </p>
        </div>

        <TextPreview bytes={active.plaintext} />

        <FieldGroup>
          <Field
            label="chk_key"
            value={toHex(active.encrypted.chkKey)}
            tone="key"
            hint="SHA256 of this chunk's plaintext. A bearer secret — it never goes to a server."
          />
          <Field
            label="blob_hash"
            value={toHex(active.encrypted.blobHash)}
            tone="hash"
            hint="SHA256 of the ciphertext. This is the address the chunk is stored and fetched by."
          />
          <Field
            label="sizes"
            value={`${active.plaintext.length} B plaintext → ${active.encrypted.ciphertext.length} B ciphertext (+16 B GCM tag)`}
            copyable={false}
          />
        </FieldGroup>

        <ByteView bytes={active.encrypted.ciphertext} label="ciphertext — the bytes that get uploaded" maxHeight="7rem" />

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              void encryptCHK(active.plaintext).then((again) => setRepeated(toHex(again.blobHash)));
            }}
            className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
          >
            Encrypt this chunk again
          </button>
          {repeated && (
            <span className="text-xs">
              {repeated === toHex(active.encrypted.blobHash) ? (
                <span className="text-emerald-600 dark:text-emerald-400">
                  Identical blob hash — that is deduplication working.
                </span>
              ) : (
                <span className="text-red-600 dark:text-red-400">Different hash, which should be impossible.</span>
              )}
            </span>
          )}
        </div>
      </div>

      <FieldGroup>
        <Field
          label="blossom URI"
          value={chkBlossomUri(active.encrypted.blobHash, active.encrypted.chkKey, "bin")}
          hint="BUD-10 plus BUD-15 parameters. Because it carries k, this must never be sent to a server."
        />
      </FieldGroup>

      <SpecNote>
        CHK reveals plaintext equality: anyone who sees two identical blob hashes knows the underlying bytes are
        identical. That is the deliberate trade for dedup, and it is why the spec warns against using CHK alone for
        low-entropy or guessable content.
      </SpecNote>

      <button
        type="button"
        onClick={onNext}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        {chunks.length > 1 ? "Build the file manifest" : "Put it in a folder"}
      </button>
    </Slide>
  );
}
